import { OperationVariables } from '@apollo/client/index.js'

export interface FormattedError {
  code: string
  message: string
  queryName: string
  variables: string
  location: string
  userId: string
}

export function formatError(e: any, queryName: string, queryVariables: OperationVariables, location: string, userId: string): FormattedError {
  const { code = '', message = '', variables = '' } = buildError(e, queryName, queryVariables)

  return {
    code,
    queryName,
    message,
    variables,
    location,
    userId,
  }
}

export const AUTH_ERROR_CODES = ['AUTH_TOKEN_EXPIRED', 'AUTH_NO_HEADER_FOUND'] as const

export const SALON_MEMBER_HAS_UPCOMING_BOOKINGS = 'SALON_MEMBER_HAS_UPCOMING_BOOKINGS'
export const BOOKING_REMINDER_NOT_SENT = 'BOOKING_REMINDER_NOT_SENT'

// Codes a caller catches and turns into its own UI, so the generic toast stays out of the way.
export const CALLER_HANDLED_ERROR_CODES: string[] = [SALON_MEMBER_HAS_UPCOMING_BOOKINGS, BOOKING_REMINDER_NOT_SENT]

/** Apollo Client 4+ may throw ServerError for non-2xx before building CombinedGraphQLErrors; GraphQL errors stay in the JSON body. */
function getErrorsFromHttpResponseBody(error: any): any[] | false {
  const bodyText: unknown =
    error?.bodyText ??
    error?.networkError?.bodyText ??
    error?.cause?.bodyText
  if (typeof bodyText !== 'string' || !bodyText.trim()) return false
  try {
    const parsed = JSON.parse(bodyText) as { errors?: unknown }
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) return parsed.errors as []
  } catch {
    return false
  }
  return false
}

function getGraphQLErrorList(error: any): any[] | false {
  // Apollo Client (classic): thrown ApolloError has graphQLErrors[]
  const classic = error?.graphQLErrors
  if (classic?.length > 0) return classic

  // Apollo Client (modern): GraphQL errors are wrapped in CombinedGraphQLErrors with errors[]
  const combined = error?.errors
  if (combined?.length > 0 && combined[0]?.message !== undefined) {
    return combined
  }

  // Sometimes surfaced via networkError.result.errors (older transports / proxies)
  const fromNetwork = error?.networkError?.result?.errors
  if (fromNetwork?.length > 0) return fromNetwork

  const fromBody = getErrorsFromHttpResponseBody(error)
  if (fromBody) return fromBody

  return false
}

export function buildError(e: any, queryName: string, queryVariables: OperationVariables) {
  const graphQLError = getGraphError(e)
  const hasInvalidGraphError = !graphQLError?.extensions
  const stringifedVariables = JSON.stringify(queryVariables)

  const authCode = AUTH_ERROR_CODES.find(code => code === graphQLError?.extensions?.code)

  if (authCode) {
    return buildAuthError(authCode, queryName, stringifedVariables)
  } else if (hasInvalidGraphError) {
    return buildServerError(e, queryName, stringifedVariables)
  } else {
    return buildGraphQLError(e, queryName, stringifedVariables)
  }
}

export function buildServerError(error: any, queryName: string, queryVariables: string) {
  const graphQLError = getGraphError(error)
  if (graphQLError?.extensions?.code) {
    return {
      error: 'Server Error',
      message: graphQLError.message,
      code: graphQLError.extensions.code,
      queryName,
      variables: queryVariables,
    }
  }

  const message = error.message ||
    (error.networkError && error.networkError.message) ||
    'An unknown error message occurred'

  return {
    error: 'Server Error',
    message,
    code: 'SERVER_CONNECTION_ERROR',
    queryName,
    variables: queryVariables,
  }
}

export function buildAuthError(code: string, queryName: string, queryVariables: string) {
  return {
    error: 'Auth error',
    message: code,
    code,
    queryName,
    variables: queryVariables,
  }
}

export function buildGraphQLError(e: any, queryName: string, queryVariables: string) {
  const list = getGraphQLErrorList(e)
  if (!list) {
    return buildServerError(e, queryName, queryVariables)
  }

  const first = list.find((err: any) => err?.extensions?.code) ?? list[0]
  const error = first
  const message = first.message
  const code = first.extensions?.code

  return {
    error,
    message,
    code,
    queryName,
    variables: queryVariables,
  }
}

export function getGraphError(error: any) {
  const graphErrors = getGraphQLErrorList(error)
  if (graphErrors && graphErrors.length > 0) {
    const withCode = graphErrors.find((e: any) => e?.extensions?.code)
    if (withCode) return withCode
    return graphErrors[0]
  }

  return false
}
